import { useQuery } from "@tanstack/react-query";
import {
  getChangeManagementPlanQueryKey,
  getChangeManagementPlansQueryKey,
} from "src/config/request-keys";
import {
  ChangeManagementPlanListItem,
  getChangeManagementPlan,
  getChangeManagementPlans,
} from "src/services/backend";
import {
  ChangeManagementPlanRecord,
  parseChangeManagementPlan,
} from "src/types/change-management";

const toPlanRecord = ({
  id,
  createdAt,
  plan,
}: ChangeManagementPlanListItem): ChangeManagementPlanRecord | null => {
  const parsed = parseChangeManagementPlan(plan);
  return parsed && { id, createdAt, plan: parsed };
};

export const useGetChangeManagementPlans = ({ orgId }: { orgId: string }) =>
  useQuery({
    queryKey: getChangeManagementPlansQueryKey(orgId),
    queryFn: async () => {
      const { data } = await getChangeManagementPlans(orgId);
      return data.map(toPlanRecord).filter((record) => record !== null);
    },
    enabled: !!orgId,
  });

/** Resolves to null when the plan does not exist, is in another organization or was saved in an older format. */
export const useGetChangeManagementPlan = ({
  orgId,
  planId,
}: {
  orgId: string;
  planId: string;
}) =>
  useQuery({
    queryKey: getChangeManagementPlanQueryKey(orgId, planId),
    queryFn: async () => {
      const response = await getChangeManagementPlan(orgId, planId);
      if (response.status === 404) return null;
      return toPlanRecord(response.data);
    },
    enabled: !!orgId && !!planId,
  });
